import {Helmet} from '@dr.pogodin/react-helmet';
import {openDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {head} from 'ramda';
import {isArray, isNotNaN, noop} from 'ramda-adjunct';

import {useAccPostDeleteErrorLogsMutation, useReprocessErrorLogMutation} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {DeleteErrorLogsDialogBody} from './components/DeleteErrorLogsDialogBody';

const PREDEFINED_COLUMNS = ['document_id'] as const;

export function AccountingErrorLog() {
  const navigate = useNavigate();
  const [reprocessErrorLog] = useReprocessErrorLogMutation();
  const [deleteErrorLog] = useAccPostDeleteErrorLogsMutation();

  const handleActionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
    refreshData,
    deselectAll,
  }) => {
    const firstErrorLogId = isArray(rowId) ? head(rowId) : rowId;
    const documentId = getColumnData<string>('document_id')?.value;

    const handleReprocess = async () => {
      if (!documentId || !firstErrorLogId) {
        return;
      }

      await reprocessErrorLog({errorLogId: firstErrorLogId, documentId})
        .unwrap()
        .then(() => refreshData())
        .catch(handleApiError);
    };

    const handleDeleteLogs = async (shouldDeleteUnresolved: boolean) => {
      const errorLogIds = isArray(rowId) ? rowId : [rowId];
      const parsedRowIds = errorLogIds.map(Number).filter(isNotNaN);

      await deleteErrorLog({id: parsedRowIds, deleteAll: shouldDeleteUnresolved})
        .unwrap()
        .then(() => showNotification.success(i18n.t('general.notifications.successfullyDeleted')))
        .then(deselectAll)
        .then(() => refreshData())
        .catch(handleApiError);
    };

    match(actionKey)
      .with('redirectDetail', () =>
        navigate(
          composePath(settingsRoutes.accountingErrorLogDetail, {
            params: {id: firstErrorLogId},
          })
        )
      )
      .with('reprocess', handleReprocess)
      .with('delete', () =>
        openDialog(
          <DeleteErrorLogsDialogBody
            onConfirm={handleDeleteLogs}
            data-testid={testIds.settings.accountingErrorLog('delete')}
          />,
          {title: i18n.t('page.accounting.labels.deleteErrorLogsTitle')}
        )
      )
      .otherwise(noop);
  };

  const title = i18n.t('page.accounting.labels.accountingErrorLog');

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate
        data-testid={testIds.settings.accountingErrorLog('header')}
        header={{
          title,
        }}
      >
        <DmsDataGrid
          gridCode="error-log"
          actionCallback={handleActionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          data-testid={testIds.settings.accountingErrorLog('grid')}
        />
      </SettingsTemplate>
    </>
  );
}
